import React, { createContext, useCallback, useContext, useEffect, useReducer } from 'react';
import type { DeliveryAddress } from '../services/ordersApi';

const CART_STORAGE_KEY = 'theory-cart-v1';

export interface MenuItem {
  id: string;
  name: string;
  description?: string;
  price?: number;
  image?: string;
  isUnavailable: boolean;
}

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  comment?: string;
  source: 'local' | 'api'; // local - из hardcode, api - из iiko
}

interface CartState {
  items: CartItem[];
  pendingRemovals: CartItem[];
  selectedTerminalGroupId: string;
  deliveryMode: 'pickup' | 'delivery';
  deliveryAddress: DeliveryAddress | null;
  deliveryTerminalGroupId: string;
}

const emptyCartState: CartState = {
  items: [],
  pendingRemovals: [],
  selectedTerminalGroupId: '',
  deliveryMode: 'pickup',
  deliveryAddress: null,
  deliveryTerminalGroupId: '',
};

function loadCartState(): CartState {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return emptyCartState;
    const parsed = JSON.parse(raw) as Partial<CartState>;
    return {
      ...emptyCartState,
      items: Array.isArray(parsed.items) ? parsed.items : [],
      selectedTerminalGroupId:
        typeof parsed.selectedTerminalGroupId === 'string' ? parsed.selectedTerminalGroupId : '',
      deliveryMode: parsed.deliveryMode === 'delivery' ? 'delivery' : 'pickup',
      deliveryAddress: parsed.deliveryAddress ?? null,
      deliveryTerminalGroupId:
        typeof parsed.deliveryTerminalGroupId === 'string' ? parsed.deliveryTerminalGroupId : '',
      pendingRemovals: [],
    };
  } catch {
    return emptyCartState;
  }
}

type CartAction =
  | { type: 'ADD'; item: CartItem }
  | { type: 'REMOVE'; id: string }
  | { type: 'RESTORE'; id: string }
  | { type: 'CONFIRM_REMOVAL'; id: string }
  | { type: 'INCREMENT'; id: string }
  | { type: 'DECREMENT'; id: string }
  | { type: 'CLEAR' }
  | { type: 'SET_TERMINAL_GROUP'; id: string | ((prev: string) => string) }
  | { type: 'SET_DELIVERY_MODE'; mode: 'pickup' | 'delivery' }
  | { type: 'SET_DELIVERY_ADDRESS'; address: DeliveryAddress | null; terminalGroupId?: string };

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD': {
      const existing = state.items.find(i => i.id === action.item.id);
      if (existing) {
        return {
          ...state,
          items: state.items.map(i =>
            i.id === action.item.id
              ? {
                  ...i,
                  quantity: i.quantity + action.item.quantity,
                  image: i.image ?? action.item.image,
                }
              : i
          ),
        };
      }
      return { ...state, items: [...state.items, action.item] };
    }
    case 'REMOVE': {
      const item = state.items.find(i => i.id === action.id);
      if (!item) return state;
      return {
        ...state,
        items: state.items.filter(i => i.id !== action.id),
        pendingRemovals: [...state.pendingRemovals, item],
      };
    }
    case 'RESTORE': {
      const item = state.pendingRemovals.find(i => i.id === action.id);
      if (!item) return state;
      const existing = state.items.find(i => i.id === action.id);
      return {
        ...state,
        items: existing
          ? state.items.map(i =>
              i.id === action.id ? { ...i, quantity: i.quantity + item.quantity } : i
            )
          : [...state.items, item],
        pendingRemovals: state.pendingRemovals.filter(i => i.id !== action.id),
      };
    }
    case 'CONFIRM_REMOVAL':
      return {
        ...state,
        pendingRemovals: state.pendingRemovals.filter(i => i.id !== action.id),
      };
    case 'INCREMENT':
      return {
        ...state,
        items: state.items.map(i =>
          i.id === action.id ? { ...i, quantity: i.quantity + 1 } : i
        ),
      };
    case 'DECREMENT':
      return {
        ...state,
        items: state.items
          .map(i => i.id === action.id ? { ...i, quantity: i.quantity - 1 } : i)
          .filter(i => i.quantity > 0),
      };
    case 'CLEAR':
      return { ...state, items: [], pendingRemovals: [] };
    case 'SET_TERMINAL_GROUP': {
      const id =
        typeof action.id === 'function'
          ? action.id(state.selectedTerminalGroupId)
          : action.id;
      if (id === state.selectedTerminalGroupId) return state;
      return { ...state, selectedTerminalGroupId: id };
    }
    case 'SET_DELIVERY_MODE':
      return { ...state, deliveryMode: action.mode };
    case 'SET_DELIVERY_ADDRESS':
      return {
        ...state,
        deliveryAddress: action.address,
        deliveryTerminalGroupId: action.terminalGroupId ?? state.deliveryTerminalGroupId,
      };
    default:
      return state;
  }
}

interface CartContextValue {
  items: CartItem[];
  pendingRemovals: CartItem[];
  total: number;
  totalCount: number;
  selectedTerminalGroupId: string;
  deliveryMode: 'pickup' | 'delivery';
  deliveryAddress: DeliveryAddress | null;
  deliveryTerminalGroupId: string;
  add: (item: CartItem) => void;
  remove: (id: string) => void;
  restore: (id: string) => void;
  confirmRemoval: (id: string) => void;
  increment: (id: string) => void;
  decrement: (id: string) => void;
  clear: () => void;
  setSelectedTerminalGroupId: (id: string | ((prev: string) => string)) => void;
  setDeliveryMode: (mode: 'pickup' | 'delivery') => void;
  setDeliveryAddress: (address: DeliveryAddress | null, terminalGroupId?: string) => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, undefined, loadCartState);

  useEffect(() => {
    try {
      localStorage.setItem(
        CART_STORAGE_KEY,
        JSON.stringify({
          items: state.items,
          selectedTerminalGroupId: state.selectedTerminalGroupId,
          deliveryMode: state.deliveryMode,
          deliveryAddress: state.deliveryAddress,
          deliveryTerminalGroupId: state.deliveryTerminalGroupId,
        }),
      );
    } catch {
      /* ignore quota / private mode */
    }
  }, [
    state.items,
    state.selectedTerminalGroupId,
    state.deliveryMode,
    state.deliveryAddress,
    state.deliveryTerminalGroupId,
  ]);

  const total = state.items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const totalCount = state.items.reduce((sum, i) => sum + i.quantity, 0);

  const setSelectedTerminalGroupId = useCallback(
    (id: string | ((prev: string) => string)) => dispatch({ type: 'SET_TERMINAL_GROUP', id }),
    [],
  );
  const setDeliveryMode = useCallback(
    (mode: 'pickup' | 'delivery') => dispatch({ type: 'SET_DELIVERY_MODE', mode }),
    [],
  );
  const setDeliveryAddress = useCallback(
    (address: DeliveryAddress | null, terminalGroupId?: string) =>
      dispatch({ type: 'SET_DELIVERY_ADDRESS', address, terminalGroupId }),
    [],
  );

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        pendingRemovals: state.pendingRemovals,
        total,
        totalCount,
        selectedTerminalGroupId: state.selectedTerminalGroupId,
        deliveryMode: state.deliveryMode,
        deliveryAddress: state.deliveryAddress,
        deliveryTerminalGroupId: state.deliveryTerminalGroupId,
        setSelectedTerminalGroupId,
        setDeliveryMode,
        setDeliveryAddress,
        add: (item) => dispatch({ type: 'ADD', item }),
        remove: (id) => dispatch({ type: 'REMOVE', id }),
        restore: (id) => dispatch({ type: 'RESTORE', id }),
        confirmRemoval: (id) => dispatch({ type: 'CONFIRM_REMOVAL', id }),
        increment: (id) => dispatch({ type: 'INCREMENT', id }),
        decrement: (id) => dispatch({ type: 'DECREMENT', id }),
        clear: () => dispatch({ type: 'CLEAR' }),
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
